import { largeTabletMax, tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import styled, { css, DefaultTheme } from 'styled-components';
import { Heading, Text } from '../typography';

const GAP = 30;
const DESKTOP_COLS = 3;
const TABLET_COLS = 2;

export type ProductsBadge = {
  color: keyof DefaultTheme;
  title: string;
  text?: string;
};

export const ProductsGridItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;
`;

export const ProductsGrid = styled.ul<{ rowGap?: number; columnGap?: number }>`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  justify-content: center;
  gap: ${({ rowGap }) => rowGap || 40}px ${GAP}px;
  width: 100%;
  padding: 0;
  margin: 0 auto;
  max-width: 1208px;

  ${ProductsGridItem} {
    flex-basis: calc((100% - (${DESKTOP_COLS} - 1) * ${({ columnGap }) => columnGap || GAP}px) / ${DESKTOP_COLS});

    @media (${largeTabletMax}) {
      flex-basis: calc((100% - (${TABLET_COLS} - 1) * ${({ columnGap }) => columnGap || GAP}px) / ${TABLET_COLS});
    }

    @media (${tabletMax}) {
      flex-basis: 100%;
    }
  }

  @media (${tabletMax}) {
    gap: 60px;
  }
`;

export const ProductBadge = styled.div<{ color: ProductsBadge['color'] }>`
  position: absolute;
  width: 80px;
  height: 80px;
  top: -15px;
  right: -15px;
  transform: rotate(11deg);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);
  background-color: ${({ theme, color }) => theme[color]};
  color: ${({ theme }) => theme.white};
  border-radius: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 2px;

  .badge-title {
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
  }

  .badge-text {
    font-size: 12px;
    line-height: 1;
  }

  @media (${largeTabletMax}) {
    right: -10px;
  }
`;

export const WhyProductsGridItem = styled(ProductsGridItem)<{ reverse?: boolean; maxW?: number; gap?: number }>`
  gap: 20px;

  svg {
    max-height: 70px;
    height: 100%;
  }

  & > div {
    display: flex;
    flex-direction: ${({ reverse }) => (reverse ? 'column-reverse' : 'column')};
    align-items: center;
    gap: ${({ gap }) => (gap != null ? gap : 12)}px;
  }

  ${Heading} {
    font-size: 44px;
  }

  ${Text} {
    color: #999;
    font-size: 22px;

    ${({ maxW }) =>
      maxW
        ? css`
            margin: 0 auto;
            max-width: ${maxW}px;
          `
        : ''}
  }
`;

export const AgentsGridItem = styled(ProductsGridItem)<{ reverse?: boolean; maxW?: number }>`
  padding: 20px 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.1);
  gap: 15px;
  justify-content: space-between;

  svg {
    max-height: 60px;
    height: 100%;
  }

  & > div:first-child {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 25px;

    & > div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  ${Heading} {
    font-size: 24px;
  }

  ${Text} {
    color: ${({ theme }) => theme.textLight};

    ${({ maxW }) =>
      maxW
        ? css`
            margin: 0 auto;
            max-width: ${maxW}px;
          `
        : ''}
  }

  a {
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.backgroundLighter};
    padding-top: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: underline;
    color: ${({ theme }) => theme.primary};
    gap: 5px;
    transition: color linear 300ms;

    svg {
      font-size: 12px;
    }

    &:hover {
      color: ${({ theme }) => theme.textLight};
    }
  }

  @media (${tabletMax}) {
    max-width: 400px;
    margin: 0 auto;
  }
`;
